import { Component, OnInit } from '@angular/core';
import { NotificationsService } from '@mt-ng2/notifications-module';
import { PublicCheckInService } from '../public-check-in/services/public-check-in.service';
import { IUser } from '../../model/interfaces/user';
import { ActivatedRoute, Router } from '@angular/router';
import { IPickup } from '../../model/interfaces/pickup';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { PickupStatus } from '../../orders/order-basic-info/pickup-status-enum';
import { IModalResult, ModalService } from '@mt-ng2/modal-module';
import { CheckInStatuses } from '../../model/CheckInStatuses';
import { CommonFunctions } from '../../common/services/common-functions.service';
import { IOffice } from '../../model/interfaces/office';
import { PickupService } from '../../model/shared-entities/pickups/pickup.service';
import { ICreditEligibility, IProcessPickupCredit } from '../../model/interfaces/pickup-credit';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../environments/environment';

@Component({
    selector: 'app-driver-check-in-add-pickups',
    templateUrl: './driver-check-in-add-pickups.component.html',
    styleUrls: ['./driver-check-in-add-pickups.component.css'],
    standalone: false
})
export class DriverCheckInAddPickupsComponent implements OnInit {
    driver: IUser;
    findPickupForm: FormGroup;
    pickups: IPickup[] = [];
    pickupNumber: string;
    pickupRemainingBalance: number;
    pickupCheckInAlreadyExists = false;
    showPrefix = false;

    constructor(
        private formBuilder: FormBuilder,
        private checkInService: PublicCheckInService,
        private pickupService: PickupService,
        private notificationService: NotificationsService,
        private router: Router,
        private route: ActivatedRoute,
        private modalService: ModalService,
    ) {}

    private log(message: string, data?: unknown): void {
        if (environment.logger) {
            if (data !== undefined) {
                 
                console.log(message, data);
            } else {
                 
                console.log(message);
            }
        }
    }

    private logError(message: string, error?: unknown): void {
        if (environment.logger) {
            if (error !== undefined) {
                 
                console.error(message, error);
            } else {
                 
                console.error(message);
            }
        }
    }

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            this.pickupNumber = params['pickupNumber'] ?? '';
            this.checkInService.checkedInDriver$.subscribe((driver) => {
                if (!driver) {
                    void this.router.navigate(['public/driver-check-in-info']);
                    return;
                }

                this.driver = driver;
                this.initForm(this.pickupNumber);

                this.findPickupForm.get('pickupNumber')?.valueChanges.subscribe((value) => {
                    if (value && value.length > 0) {
                        this.showPrefix = true;
                    } else {
                        this.showPrefix = false;
                    }
                });
            });
        });
    }

    onSubmit() {
        const formPickupNumber = this.findPickupForm.get('pickupNumber').value as string;

        const fullPickupNumber = formPickupNumber.toUpperCase().startsWith('PK-') ? formPickupNumber : `PK-${formPickupNumber}`;

        this.checkInService.findValidPickup(fullPickupNumber).subscribe({
            next: (pickup) => {
                if (!pickup) {
                    this.notificationService.error('We were not able to find a pickup scheduled for today with the provided pickup number.');
                    return;
                }

                if (pickup.StatusId !== PickupStatus.Confirmed && pickup.StatusId !== PickupStatus.CheckedIn) {
                    this.notificationService.error(`The pickup ${fullPickupNumber} is not confirmed yet.`);
                    return;
                }

                if (pickup.DateConfirmed && !CommonFunctions.isToday(pickup.DateConfirmed.toString())) {
                    const dateToDisplay = new Date(pickup.DateConfirmed);
                    this.notificationService.error(
                        `The pickup number ${fullPickupNumber} is scheduled for ${dateToDisplay.toDateString()} ${dateToDisplay.toLocaleTimeString()}.`,
                    );
                    return;
                }

                const orders = pickup.Orders;
                if (!orders || orders.length < 0) {
                    this.processPickup(pickup);
                    return;
                }

                const office = orders[0].Warehouse?.Office;
                const location = office?.Location;
                if (!office || office.CheckInRadiusLimit <= 0 || !location) {
                    this.processPickup(pickup);
                    return;
                }

                this.getCoordinates({ highAccuracy: true })
                    .then((coords: GeolocationCoordinates) => {
                        const distance = this.getDistanceFromLatLonInMiles(
                            coords.latitude,
                            coords.longitude,
                            location.EntranceLatitude,
                            location.EntranceLongitude,
                        );
                        if (distance > office.CheckInRadiusLimit) {
                            this.modalService
                                .showModal({
                                    title: `Check-in is not allowed`,
                                    icon: 'error',
                                    html: `<p>We were not able to locate you within ${office.CheckInRadiusLimit} mile(s) of your check-in destination. Please get closer and try again.</p>
                                               <p>If you need help, please call us: <a href="tel:8564723142">(856) 742-3142</a></p>`,
                                    confirmButtonText: 'OK',
                                })
                                .subscribe();
                            return;
                        }
                        this.processPickup(pickup);
                    })
                    .catch((error: GeolocationPositionError) => {
                        if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
                            this.modalService
                                .showModal({
                                    title: error.message,
                                    icon: 'error',
                                    html: `<p>Please allow your browser to track your location.</p>`,
                                    confirmButtonText: 'OK',
                                })
                                .subscribe();
                            return;
                        }
                        this.notificationService.error(`Error trying to get user's coordinates.`);
                    });
            },
            error: (errorResponse: HttpErrorResponse) => {
                this.notificationService.error(errorResponse.error['ModelState'] as string);
            },
        });
    }

    initForm(pickupNumber: string): void {
        // Handle pre-filled pickup numbers that might include the "PK-" prefix
        let formattedPickupNumber = pickupNumber;
        if (pickupNumber && pickupNumber.toUpperCase().startsWith('PK-')) {
            // Remove the "PK-" prefix since the form field will add it via the prefix attribute
            formattedPickupNumber = pickupNumber.substring(3);
        }

        this.findPickupForm = this.formBuilder.group({
            pickupNumber: [formattedPickupNumber, [Validators.required]],
        });

        // Set showPrefix based on whether we have a value
        this.showPrefix = formattedPickupNumber && formattedPickupNumber.length > 0;
    }

    navigateToGoogleMaps(pickup: IPickup) {
        this.checkInService.getPickupLocation(this.driver.Id, pickup.Id).subscribe({
            next: (location) => {
                if (!location?.EntranceLatitude || !location?.EntranceLongitude) {
                    this.notificationService.error("This pickup does not have entrance coordinates entered for it's office location.");
                    return;
                }
                const url = `https://www.google.com/maps/search/?api=1&query=${location.EntranceLatitude},${location.EntranceLongitude}`;
                window.open(url, '_blank');
            },
            error: () => {
                this.notificationService.error('Error trying to get pickup location.');
            },
        });
    }

    goToCreatePickupPage(): void {
        this.checkInService.checkedInDriver$.next(this.driver);
        void this.router.navigate([`public/driver-check-in-create-pickup`]);
    }

    private processPickup(pickup: IPickup) {
        this.checkInService.getRemainingBalance(pickup.Id).subscribe((remainingBalance) => {
            this.pickupRemainingBalance = remainingBalance;
            if (
                (pickup.CheckIns && pickup.CheckIns[0]?.StatusId === CheckInStatuses.DriverNotified) ||
                pickup.StatusId === PickupStatus.DriverNotified
            ) {
                this.checkInService.sendDriverNotifiedSms(this.driver.Id, pickup.Id).subscribe({
                    next: (pickup) => {
                        this.checkInService.checkedInDriver$.next(this.driver);
                        this.checkInService.checkedInPickup$.next(pickup);
                        void this.router.navigate([`public/driver-notified`]);
                    },
                    error: () => {
                        this.notificationService.error('Error trying to send driver notified SMS.');
                    },
                });
                return;
            }

            if (pickup.StatusId === PickupStatus.CheckedIn && this.pickupRemainingBalance === 0) {
                this.checkInService.checkedInDriver$.next(this.driver);
                this.checkInService.checkedInPickup$.next(pickup);
                void this.router.navigate([`public/driver-checked-in`]);
                return;
            }

            if (pickup.StatusId === PickupStatus.Requested && this.pickupRemainingBalance === 0) {
                this.checkInService.checkedInDriver$.next(this.driver);
                this.checkInService.checkedInPickup$.next(pickup);
                void this.router.navigate([`public/driver-check-in-edit-pickup`]);
                return;
            }

            if (pickup.StatusId === PickupStatus.Fulfilled && this.pickupRemainingBalance === 0) {
                this.checkInService.checkedInDriver$.next(this.driver);
                this.checkInService.checkedInPickup$.next(pickup);
                void this.router.navigate([`public/driver-notified`]);
                return;
            }

            if (pickup.StatusId !== PickupStatus.Confirmed && this.pickupRemainingBalance === 0) {
                this.notificationService.error('This pickup has not been confirmed yet.');
                return;
            }

            if (this.pickups.some((p) => p.Id === pickup.Id) && this.pickupRemainingBalance === 0) {
                this.notificationService.error('This pickup has already been entered.');
                return;
            }

            // Always call createCheckInForDriver — the endpoint handles both creating a new
            // check-in and updating the carrier/credit on an existing one.
            this.checkInService.createCheckInForDriver(this.driver.Id, pickup.Id).subscribe({
                next: (checkIn) => {
                    pickup.CheckIns = [checkIn];
                    this.checkInService.getRemainingBalance(pickup.Id).subscribe((remainingBalance) => {
                        this.pickupRemainingBalance = remainingBalance;
                        this.showModalForFoundPickup(pickup);
                    });
                },
                error: (errorResponse: HttpErrorResponse) => {
                    const msg =
                        typeof errorResponse.error === 'string' ? errorResponse.error : 'An error occurred while checking in. Please try again.';
                    this.notificationService.error(msg);
                },
            });
        });
    }

    private showModalForFoundPickup(pickup: IPickup): void {
        this.modalService
            .showModal({
                title: 'Found It!',
                html: this.determineFoundPickupDialogMessage(pickup),
                confirmButtonText: 'OK',
                denyButtonText: 'Edit Orders',
                showDenyButton: true,
                showCancelButton: true,
                denyButtonColor: '#7066e0',
                cancelButtonColor: '#7066e0',
                cancelButtonText: 'Close',
            })
            .subscribe((result: IModalResult) => {
                if (result.isDismissed) {
                    return;
                }

                const checkIn = pickup.CheckIns?.find((c) => c.CheckInUserId === this.driver.Id);
                this.checkInService.driverCreatedCheckIn$.next(checkIn);
                this.checkInService.checkedInDriver$.next(this.driver);
                this.checkInService.checkedInPickup$.next(pickup);
                if (result.isConfirmed && this.pickupRemainingBalance > 0) {
                    // Check if we can process payment with credit first
                    this.processPaymentWithCreditIfAvailable(pickup);
                    return;
                }

                if (result.isConfirmed && this.pickupRemainingBalance === 0) {
                    // No payment needed (already processed with credit or no charges), proceed directly
                    void this.router.navigate([`public/driver-checked-in`]);
                    return;
                }

                if (result.isDenied) {
                    void this.router.navigate([`public/driver-check-in-edit-pickup-orders`]);
                    return;
                }
            });
    }

    private determineFoundPickupDialogMessage(pickup: IPickup): string {
        let pickupDialogMessage = `
            <p>We've located the Pickup # you have entered.</p>
            <p>Select OK to proceed with payment or Edit Orders to add or remove orders from the pickup.</p>`;

        const twicCardIsCaptured = this.driver.TwicBackImageId && this.driver.TwicFrontImageId;
        const twicCardExpired = new Date(this.driver.TwicExpirationDate) < new Date();
        if (!twicCardIsCaptured || twicCardExpired) {
            pickupDialogMessage += `<br><p><b>NOTE:</b> Your TWIC card is either missing or expired. Please contact your administrator to resolve this issue.</p>`;
        }

        const office = pickup.Orders?.find((o) => o.Warehouse?.Office)?.Warehouse?.Office;
        if (office && this.isOutsideOfficeHours(office)) {
            pickupDialogMessage += `<br><p><b>NOTE:</b> This office is currently closed. Please return during normal operating hours, Monday to Friday, from 8 AM to 4 PM. Some hours may vary. For questions, call 856-742-3089.</p>`;
        }

        return pickupDialogMessage;
    }

    private getCoordinates(options?: { highAccuracy?: boolean; timeout?: number; maximumAge?: number }): Promise<GeolocationCoordinates> {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation is not supported by your browser.'));
                return;
            }

            const defaultOptions = {
                enableHighAccuracy: options?.highAccuracy ?? false,
                timeout: options?.timeout ?? 10000, // 10 seconds
                maximumAge: options?.maximumAge ?? 60000, // 1 minute
            };

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve(position.coords);
                },
                (error) => {
                    let errorMessage: string;
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = 'Location access denied by user.';
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = 'Location information is unavailable.';
                            break;
                        case error.TIMEOUT:
                            errorMessage = 'Location request timed out.';
                            break;
                        default:
                            errorMessage = 'An unknown error occurred while retrieving location.';
                            break;
                    }
                    reject(new Error(errorMessage));
                },
                defaultOptions,
            );
        });
    }

    // https://stackoverflow.com/a/27943
    private getDistanceFromLatLonInMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 3959; // Radius of the earth in miles
        const dLat = this.deg2rad(lat2 - lat1); // deg2rad below
        const dLon = this.deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c; // Distance in miles
        return d;
    }

    private deg2rad(deg: number): number {
        return deg * (Math.PI / 180);
    }

    private isOutsideOfficeHours(office: IOffice, datetime: Date = new Date()): boolean {
        // Get day of week (0 = Sunday, 1 = Monday, etc.)
        const dayOfWeek = datetime.getDay();

        // Check if the current day is a working day
        const isWorkingDay = (() => {
            switch (dayOfWeek) {
                case 0:
                    return office.DefaultWeekdaySun;
                case 1:
                    return office.DefaultWeekdayMon;
                case 2:
                    return office.DefaultWeekdayTue;
                case 3:
                    return office.DefaultWeekdayWed;
                case 4:
                    return office.DefaultWeekdayThu;
                case 5:
                    return office.DefaultWeekdayFri;
                case 6:
                    return office.DefaultWeekdaySat;
                default:
                    return false;
            }
        })();

        // If it's not a working day, we're outside office hours
        if (!isWorkingDay) {
            return true;
        }

        // Parse office hours
        const [startHour, startMinute] = office.DefaultStartTime.split(':').map(Number);
        const [endHour, endMinute] = office.DefaultEndTime.split(':').map(Number);

        // Create Date objects for start and end times on the current day
        const startTime = new Date(datetime);
        startTime.setHours(startHour, startMinute, 0);

        const endTime = new Date(datetime);
        endTime.setHours(endHour, endMinute, 0);

        // Check if current time is outside office hours
        return datetime < startTime || datetime > endTime;
    }

    private processPaymentWithCreditIfAvailable(pickup: IPickup): void {
        this.log('processPaymentWithCreditIfAvailable started for pickup:', pickup.Id);
        this.log('Current pickup remaining balance:', this.pickupRemainingBalance);

        // Check carrier credit eligibility for this pickup
        this.checkInService
            .checkCarrierCreditEligibility(pickup.Id)
            .pipe(
                switchMap((creditEligibility: ICreditEligibility) => {
                    this.log('Credit eligibility check result:', creditEligibility);

                    // If carrier has sufficient credit, process payment with credit
                    if (creditEligibility.canUseCredit && creditEligibility.availableCredit >= this.pickupRemainingBalance) {
                        this.log('Processing payment with credit - sufficient credit available');
                        return this.pickupService.processPickupWithCredit(pickup.Id);
                    } else {
                        this.log('Cannot use credit - insufficient credit or not eligible');
                        // Return null to indicate no credit processing needed
                        return of(null);
                    }
                }),
                catchError((error) => {
                    this.logError('Error checking credit eligibility:', error);
                    // Error checking credit eligibility, fallback to payment screen
                    void this.router.navigate([`public/driver-check-in-pickup-payment`]);
                    return of(null);
                }),
            )
            .subscribe((result: IProcessPickupCredit | null) => {
                this.log('Final result from credit processing:', result);

                if (result === null) {
                    this.log('No credit processing or error occurred, navigating to payment screen');
                    // No credit processing or error occurred, go to payment screen
                    void this.router.navigate([`public/driver-check-in-pickup-payment`]);
                    return;
                }

                if (result.Success) {
                    this.log('Payment processed successfully using carrier credit');
                    this.pickupRemainingBalance = 0;

                    // send pickup detail to FTP and navigate to checked-in page
                    this.checkInService.exportPickupDataViaFtp(this.driver.Id, pickup.Id).subscribe({
                        next: () => {
                            this.log('Pickup data exported via FTP successfully');
                        },
                    });
                    // send checkin notifications
                    this.checkInService.sendCheckInNotifications(pickup.CheckIns[0]?.Id).subscribe({
                        next: () => {
                            this.log('Check-in notifications sent successfully');
                        },
                    });
                    void this.router.navigate([`public/driver-checked-in`]);
                } else {
                    this.log('Credit processing failed, fallback to payment screen');
                    // Credit processing failed, fallback to payment screen
                    this.notificationService.warning('Please proceed with payment.');
                    void this.router.navigate([`public/driver-check-in-pickup-payment`]);
                }
            });
    }
}
